import { Component, DoCheck, Input, OnChanges, OnDestroy } from '@angular/core';

import * as _ from 'lodash';

import { Message } from '../../../../../models/message';
import { MessagePollBody } from '../../../../../models/message-poll-body';
import { ChatPoll, ChatPollOption, ChatPollVoter } from '../../../../../models/chat-poll';

import { ChatPollService } from '../../chat-poll/chat-poll.service';
import { PollDetailComponent } from '../../chat-poll/poll-detail/poll-detail.component';

import { UserContactService } from '../../../../services/data/user-contact/user-contact.service';
import { TnDialogService } from '../../../../../utilities/tn-dialog/tn-dialog.service';
import { LoggerService } from '../../../../../utilities/logger/logger.service';

// A type-140 vote is ephemeral, so a response frame is not guaranteed - release the card anyway.
const VOTE_RESPONSE_TIMEOUT = 5000;

// Avatars are a glance-level cue; the number carries the full count.
const VOTER_AVATAR_MAX_COUNT = 3;

/**
 * Poll card rendered for a type-14 (CHAT_POLL) message.
 *
 * Calls /chat_poll/result once when the card first renders, and again after a
 * vote is sent - /load_history is cursor based (a cached poll is never
 * re-fetched), and restricted polls send no live vote updates to ordinary
 * members, so a fresh read is the only reliable source of counts.
 */
@Component({
  selector: 'tn-poll-message',
  templateUrl: './poll-message.component.html',
  styleUrls: ['./poll-message.component.scss']
})
export class PollMessageComponent implements OnChanges, DoCheck, OnDestroy {
  @Input() message: Message;
  @Input() chatId: string;
  // Rendered outside the timeline (e.g. the starred list), where the card is a preview only:
  // results stay visible but voting and ending are not offered.
  @Input() isReadOnly: boolean;

  body: MessagePollBody;

  // local voting selection, kept in sync with poll.my_option_ids
  selectedOptionIds: string[] = [];

  isSubmittingVote: boolean = false;

  private _hasFetchedInitialResult: boolean = false;
  private _voteTimeoutTimer: any = null;
  private _syncedUpdatedAt: number = undefined;

  constructor(
    private _chatPollService: ChatPollService,
    private _userContactService: UserContactService,
    private _tnDialogService: TnDialogService,
    private _loggerService: LoggerService
  ) { }

  get poll(): ChatPoll {
    return this.message ? this.message.poll : null;
  }

  get totalVoters(): number {
    return this.poll && this.poll.total_voters ? this.poll.total_voters : 0;
  }

  get isVotingDisabled(): boolean {
    return this.isReadOnly || !this.poll || this.poll.is_ended || this.isSubmittingVote;
  }

  /**
   * A poll delivered live carries no `poll` state, so until the first /chat_poll/result comes
   * back the only identity available is the poll_id echoed into the message body (or, before
   * even that, the message_id).
   */
  get pollId(): string {
    if (this.poll) {
      return this.poll.poll_id;
    }
    return this.body ? this.body.poll_id : null;
  }

  /**
   * Options to render: prefer the poll state (it carries vote_count), and fall back to the
   * message body so a freshly created poll shows its options immediately rather than blank.
   */
  get displayOptions(): ChatPollOption[] {
    if (this.poll && this.poll.options && this.poll.options.length > 0) {
      return this.poll.options;
    }
    if (!this.body || !this.body.options) {
      return [];
    }
    // A poll we just sent still carries the create body, whose options are plain strings
    // (the server assigns the option ids and echoes them back), so normalise both shapes.
    return _.map(this.body.options, (option: any, index: number) => {
      if (_.isString(option)) {
        return <ChatPollOption>{ option_id: null, content: option, order: index };
      }
      return <ChatPollOption>option;
    });
  }

  ngOnChanges(): void {
    this.readMessage();
  }

  /**
   * The chat message cache is updated in place (`_.assign` onto the existing object), so the
   * @Input reference never changes and ngOnChanges does not fire when the server echo replaces
   * a just-sent poll's body. Re-read on every check instead - it is a couple of comparisons.
   */
  ngDoCheck(): void {
    this.readMessage();
  }

  private readMessage(): void {
    if (!this.message) {
      return;
    }
    this.body = this.message.parsedBody;
    // Let the service apply live updates straight onto the object this card renders.
    this._chatPollService.registerPollMessage(this.message);

    if (this.poll && this.poll.updated_at !== this._syncedUpdatedAt) {
      this._syncedUpdatedAt = this.poll.updated_at;
      this.syncSelectionFromPoll();
    }

    // Fetch even when `poll` is absent - a live-delivered poll arrives without it, and without
    // this the card could never populate.
    if (!this._hasFetchedInitialResult && (this.pollId || this.message.message_id)) {
      this._hasFetchedInitialResult = true;
      this.refreshPollResult();
    }
  }

  ngOnDestroy(): void {
    clearTimeout(this._voteTimeoutTimer);
    if (this.message && this.message.message_id) {
      this._chatPollService.unregisterPollMessage(this.message.message_id);
    }
  }

  syncSelectionFromPoll(): void {
    let myOptionIds = this.poll && this.poll.my_option_ids ? this.poll.my_option_ids : [];
    this.selectedOptionIds = _.clone(myOptionIds);
  }

  isOptionSelected(optionId: string): boolean {
    return this.selectedOptionIds.indexOf(optionId) !== -1;
  }

  /**
   * Tapping an option votes for it; tapping an already-selected one takes the vote back.
   * A vote always replaces the member's whole selection, so a single-choice retract is an
   * empty selection and a multiple-choice retract just drops that one option.
   */
  onOptionClick(optionId: string): void {
    if (this.isVotingDisabled) {
      return;
    }

    if (this.isOptionSelected(optionId)) {
      this.submitVote(_.without(this.selectedOptionIds, optionId));
      return;
    }

    if (this.body && this.body.allow_multiple_choice) {
      this.submitVote(_.union(this.selectedOptionIds, [optionId]));
    } else {
      this.submitVote([optionId]);
    }
  }

  /**
   * Voters are only present on poll state fetched from /chat_poll/result, and only when voter
   * identity is visible to this viewer, so the avatars are simply absent otherwise.
   *
   * The resolved result flag is re-checked here so a restricted poll fails closed: who voted is a
   * bigger leak than how many did, and the card should not depend on the server having withheld
   * `voters` to keep it back.
   */
  getOptionVoters(option: ChatPollOption): ChatPollVoter[] {
    if (!this.poll || this.poll.should_display_result !== 1) {
      return [];
    }
    if (!option.voters || option.voters.length === 0) {
      return [];
    }
    return option.voters.slice(0, VOTER_AVATAR_MAX_COUNT);
  }

  getVoterAvatar(voter: ChatPollVoter): string {
    let contact = this._userContactService.getUserContactByUserId(voter.user_id);
    return contact ? contact.avatarImageSrc : null;
  }

  getVoterName(voter: ChatPollVoter): string {
    let contact = this._userContactService.getUserContactByUserId(voter.user_id);
    return contact ? contact.name : voter.user_id;
  }

  refreshPollResult(): void {
    if (!this.pollId && !this.message.message_id) {
      return;
    }
    this._chatPollService.getPollResult(
      this.pollId,
      this.message.message_id,
      (resp: ChatPoll) => {
        this._chatPollService.mergePollResult(this.message.message_id, resp);
        this.syncSelectionFromPoll();
      },
      () => { }
    );
  }

  getOptionPercentage(option: ChatPollOption): number {
    if (!this.totalVoters || !option.vote_count) {
      return 0;
    }
    return Math.round((option.vote_count / this.totalVoters) * 100);
  }

  submitVote(optionIds: string[]): void {
    this._loggerService.log('Sending poll vote for poll: ' + this.pollId);
    this.isSubmittingVote = true;

    // optimistic local update while waiting for the round trip
    this.poll.my_option_ids = optionIds;
    this.syncSelectionFromPoll();

    // A vote is an ephemeral type-140 message, so we cannot rely on a response frame ever
    // coming back. Always release the card after VOTE_RESPONSE_TIMEOUT, otherwise a silent
    // send would leave voting permanently disabled.
    let isSettled = false;
    let settle = () => {
      if (isSettled) {
        return;
      }
      isSettled = true;
      clearTimeout(this._voteTimeoutTimer);
      this.isSubmittingVote = false;
      this.refreshPollResult();
    };

    clearTimeout(this._voteTimeoutTimer);
    this._voteTimeoutTimer = setTimeout(() => settle(), VOTE_RESPONSE_TIMEOUT);

    this._chatPollService.sendVote(
      this.chatId,
      this.pollId,
      this.message.message_id,
      optionIds,
      () => settle()
    );
  }


  openPollDetail(): void {
    this._tnDialogService.openTnDialog(PollDetailComponent, {
      message: this.message,
      chatId: this.chatId,
      isReadOnly: this.isReadOnly
    });
  }

}
